import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { Config } from '@config/config.service';
import { CookiesService } from '@services/cookies.service';

export interface SseSubscribeConfig {
  channel: string;
  path: string;
  queryParams?: Record<string, string | number | boolean>;
  eventName?: string | string[];
  withCredentials?: boolean;
  tokenQueryParamName?: string;
}

export interface SseEventEnvelope<T = any> {
  eventName: string;
  payload: T;
}

interface SseChannelState {
  subject: Subject<any>;
  eventSource: EventSource;
  eventNames: string[];
  eventHandler: (event: MessageEvent) => void;
  openHandler: () => void;
  errorHandler: (event: Event) => void;
  hasOpened: boolean;
}

@Injectable({ providedIn: 'root' })
export class SseService {
  private readonly defaultTokenQueryParamName = 'access_token';
  private channels = new Map<string, SseChannelState>();

  constructor(
    private config: Config,
    private cookiesService: CookiesService
  ) {}

  subscribe<T>(subscribeConfig: SseSubscribeConfig): Observable<T> {
    const channel = subscribeConfig.channel;
    if (!this.channels.get(channel)) {
      this.channels.set(channel, this.createChannel(channel, subscribeConfig));
    }

    return this.channels.get(channel).subject.asObservable();
  }

  unsubscribe(channel?: string): void {
    if (channel) {
      this.closeAndDeleteChannel(channel, true);
      return;
    }

    Array.from(this.channels.keys()).forEach((existingChannel: string) => {
      this.closeAndDeleteChannel(existingChannel, true);
    });
  }

  private createChannel(channel: string, subscribeConfig: SseSubscribeConfig): SseChannelState {
    const eventSourceUrl = this.buildEventSourceUrl(
      subscribeConfig.path,
      subscribeConfig.queryParams,
      subscribeConfig.tokenQueryParamName || this.defaultTokenQueryParamName
    );
    const eventNames = Array.isArray(subscribeConfig.eventName)
      ? subscribeConfig.eventName
      : [subscribeConfig.eventName || 'message'];
    const subject = new Subject<any>();
    const eventSource = this.createEventSource(
      eventSourceUrl,
      Boolean(subscribeConfig.withCredentials)
    );
    const state: SseChannelState = {
      subject,
      eventSource,
      eventNames,
      eventHandler: null,
      openHandler: null,
      errorHandler: null,
      hasOpened: false
    };
    const eventHandler = (event: MessageEvent): void => {
      const payload = this.mapSsePayload(event.data);
      if (state.eventNames.length > 1) {
        subject.next({ eventName: event.type, payload } as SseEventEnvelope);
        return;
      }
      subject.next(payload);
    };
    const openHandler = (): void => {
      state.hasOpened = true;
    };
    const errorHandler = (event: Event): void => {
      if (!state.hasOpened) {
        subject.error(event);
        this.closeAndDeleteChannel(channel, false);
        return;
      }
    };

    state.eventHandler = eventHandler;
    state.openHandler = openHandler;
    state.errorHandler = errorHandler;

    eventSource.addEventListener('open', openHandler as EventListener);
    eventNames.forEach((eventName: string) => {
      eventSource.addEventListener(eventName, eventHandler as EventListener);
    });
    eventSource.addEventListener('error', errorHandler as EventListener);
    return state;
  }

  private createEventSource(url: string, withCredentials: boolean): EventSource {
    return new EventSource(url, { withCredentials });
  }

  private mapSsePayload(payload: string): any {
    try {
      return JSON.parse(payload);
    } catch (error) {
      return payload;
    }
  }

  private buildEventSourceUrl(
    path: string,
    queryParams: Record<string, string | number | boolean> = {},
    tokenQueryParamName: string
  ): string {
    const accessToken = this.cookiesService.getCookie(this.config.accessTokenKey);
    const isAbsolutePath = /^https?:\/\//.test(path);
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const baseUrl = this.config.baseUrl ? this.config.baseUrl.replace(/\/$/, '') : '';
    const url = new URL(
      isAbsolutePath ? path : `${baseUrl}${normalizedPath}`,
      window.location.origin
    );

    Object.keys(queryParams).forEach((queryParamName: string) => {
      url.searchParams.set(queryParamName, `${queryParams[queryParamName]}`);
    });

    if (accessToken) {
      url.searchParams.set(tokenQueryParamName, accessToken);
    }

    return url.toString();
  }

  private closeAndDeleteChannel(channel: string, shouldComplete: boolean): void {
    const state = this.channels.get(channel);

    if (!state) {
      return;
    }

    state.eventSource.removeEventListener('open', state.openHandler as EventListener);
    state.eventNames.forEach((eventName: string) => {
      state.eventSource.removeEventListener(eventName, state.eventHandler as EventListener);
    });
    state.eventSource.removeEventListener('error', state.errorHandler as EventListener);
    state.eventSource.close();

    if (shouldComplete) {
      state.subject.complete();
    }

    this.channels.delete(channel);
  }
}
